<!-- 
  Add Contacts Component
  ----------------------
  This component is rendered inside the Add Contact modal for the LogFormComponent. It provides UI for selecting, managing, 
  and prioritizing contacts associated with a PO. 

  Features:
  - Displays a list of contacts from the PO, indicating whether the data is from MacBuy.
  - Shows a warning if the data is not from MacBuy (contacts might be unreliable).
  - Each contact has a checkbox to toggle inclusion in the notify list.
  - Contacts in the notify list are displayed as "chips" with:
    - An "X" to remove them.
    - A star icon to mark one as the primary contact (only one can be primary).
  - A search bar allows users to find additional contacts by email and add them to the list.

-->

<div class="subtitle-container">
  <ion-text color="primary" class="subtitle">
Search for contact emails or select them from below to populate the notify list.
    <ion-text color="danger"><ion-icon name="star"></ion-icon> </ion-text>
Star the main contact.
  </ion-text>
</div>

<!-- Search bar to find and add new contacts by email -->
<ion-searchbar
  [(ngModel)]="searchQuery"
  (ionInput)="filterContacts()"
  placeholder="Search, or type an email ({{ companyDomain }} is added for you)"
>
</ion-searchbar>

<!-- Loading, failed-with-a-reason-and-a-retry, and genuinely-no-match are three
     distinct states — an empty directory used to render as nothing at all,
     indistinguishable from a real "no results" answer. -->
<div *ngIf="employeesLoading && !employeeEmails.length" class="employee-search-loading">
  <ion-spinner name="crescent"></ion-spinner>
  <ion-text color="medium">Loading employee list...</ion-text>
</div>

<div *ngIf="employeesError && !employeesLoading" class="employee-fetch-error">
  <ion-icon name="alert-circle-outline" color="warning"></ion-icon>
  <ion-text color="dark">{{ employeesError.message }}</ion-text>
  <ion-button size="small" fill="clear" (click)="retryEmployeeFetch()">Try again</ion-button>
</div>

<!-- Dropdown list of filtered search results -->

<ion-list *ngIf="filteredContacts.length > 0">
  <ion-item
    *ngFor="let emailName of filteredContacts"
    (click)="addToNotifyList(emailName)"
  >
    {{ emailName }}
  </ion-item>
</ion-list>

<!-- An address built from the typed text (domain appended, or first.last from a
     name) that the directory doesn't list, or the directory failed to load. The
     label says how it was built so a guessed address isn't mistaken for a lookup. -->
<ion-list *ngIf="typedEmail as typed">
  <ion-item button (click)="addToNotifyList(typed.email)">
    <ion-icon name="person-add-outline" slot="start"></ion-icon>
    <ion-label>
      Add {{ typed.email }}
      <p *ngIf="typed.source === 'domain-added'">{{ companyDomain }} was added for you.</p>
      <p *ngIf="typed.source === 'from-name'">Built from the name as first.last{{ companyDomain }}. Check it's right before adding.</p>
    </ion-label>
  </ion-item>
</ion-list>

<div *ngIf="noSearchMatches" class="employee-no-match">
  <ion-text color="medium">No employee found for "{{ searchQuery }}".</ion-text>
</div>

<div class="modal-container">
  <!-- Top Half: Notify List (Selected Contacts) -->
  <div class="notify-section">
    <div class="scrollable-card">
      <div>
        <div class="chips-container">
          <!-- Empty state message if no contacts are selected -->

          <div
            *ngIf="notifyList.length === 0"
            class="empty-notify-list-container"
          >
            <ion-text color="medium">
              No emails are currently selected to be notified.
            </ion-text>
          </div>

          <!-- List of selected contacts shown as chips. Only emails that are flagged as valid are shown -->
          <ng-container *ngFor="let email of notifyList">
            <ion-chip *ngIf="email.isValidEmail">
              <ion-label>{{ email.contact }}</ion-label>
              <!-- X to Remove contact from notify list -->
              <ion-icon
                name="close-circle"
                (click)="removeFromNotifyList(email.contact)"
              ></ion-icon>
              <!-- Toggle primary contact -->
              <ion-icon
                color="danger"
                title="Select Main Contact"
                [name]="
                  email.contact === primaryContact ? 'star' : 'star-outline'
                "
                (click)="handleUpdatePrimaryContact(email.contact)"
              >
              </ion-icon>
            </ion-chip>
          </ng-container>
          <!-- Infinite scroll for long contact lists -->
          <ion-infinite-scroll> </ion-infinite-scroll>
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Half: PO-Provided Contacts -->
  <div class="po-section">
    <div class="scrollable-card">
      <div>
        <!-- Warning when an address on this PO was actually constructed from a
             name, rather than whenever the PO merely came from GP. -->
        <ion-item
          *ngIf="hasContactNeedingEmail"
          color="warning"
        >
          <ion-label>
            Warning: some contacts for this PO came from GP as names with no email
            address. Those cannot be notified — search for the person by name below
            and add their real address.
          </ion-label>
        </ion-item>
        <ion-list>
          <ion-item-group>
            <!-- PO Creator Contact (renders only if data is available) -->
            <ng-container *ngIf="allContactInfo.poCreator">
              <ion-item-divider>
                <ion-label> PO Creator:</ion-label>
              </ion-item-divider>

              <!--  contact list item component, rendering a checkbox and a star for primary contact selection -->
              <app-contact-list-item
                [contact]="allContactInfo.poCreator"
                [primaryContact]="primaryContact"
                [(emailStatus)]="
                  emailStatusMap[allContactInfo.poCreator.contact]
                "
                (emailStatusChange)="updateNotifyListFromPO($event)"
                (updatePrimaryContact)="handleUpdatePrimaryContact($event)"
                (findInDirectory)="onFindInDirectory($event)"
              >
              </app-contact-list-item>
            </ng-container>

            <!-- Requested By Contact (renders only if data is available) -->
            <ng-container *ngIf="allContactInfo.requestedBy">
              <ion-item-divider>
                <ion-label> Requested By:</ion-label>
              </ion-item-divider>
              <!--  contact list item component, rendering a checkbox and a star for primary contact selection -->
              <app-contact-list-item
                [contact]="allContactInfo.requestedBy"
                [primaryContact]="primaryContact"
                [(emailStatus)]="
                  emailStatusMap[allContactInfo.requestedBy.contact]
                "
                (emailStatusChange)="updateNotifyListFromPO($event)"
                (updatePrimaryContact)="handleUpdatePrimaryContact($event)"
                (findInDirectory)="onFindInDirectory($event)"
              >
              </app-contact-list-item>
            </ng-container>

            <!-- Ship To Attention Contact (renders only if data is available) -->
            <ng-container *ngIf="allContactInfo.shipToAttention">
              <ion-item-divider>
                <ion-label> Ship To Attention:</ion-label>
              </ion-item-divider>

              <!--  contact list item component, rendering a checkbox and a star for primary contact selection -->

              <app-contact-list-item
                [contact]="allContactInfo.shipToAttention"
                [primaryContact]="primaryContact"
                [(emailStatus)]="
                  emailStatusMap[allContactInfo.shipToAttention.contact]
                "
                (emailStatusChange)="updateNotifyListFromPO($event)"
                (updatePrimaryContact)="handleUpdatePrimaryContact($event)"
                (findInDirectory)="onFindInDirectory($event)"
              >
              </app-contact-list-item>
            </ng-container>

            <!-- Notify List (renders if there are additional contacts from the PO) -->
            <ng-container
              *ngIf="allContactInfo.notify && allContactInfo.notify.length > 0"
            >
              <ion-item-divider>
                <ion-label>Notify:</ion-label>
              </ion-item-divider>
              <!-- Each contact gets its own list item component, 
    rendering a checkbox and a star for primary contact selection -->
              <app-contact-list-item
                *ngFor="let email of allContactInfo.notify"
                [contact]="email"
                [primaryContact]="primaryContact"
                [(emailStatus)]="emailStatusMap[email.contact]"
                (emailStatusChange)="updateNotifyListFromPO($event)"
                (updatePrimaryContact)="handleUpdatePrimaryContact($event)"
                (findInDirectory)="onFindInDirectory($event)"
              >
              </app-contact-list-item>
            </ng-container>
          </ion-item-group>
        </ion-list>
        <!-- Infinite scroll for long PO contact lists -->

        <ion-infinite-scroll> </ion-infinite-scroll>
      </div>
    </div>
  </div>
</div>
